import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Steps,
  Row,
  Col,
  Descriptions,
  Tag,
  InputNumber,
  Tabs,
  List,
  Collapse,
  Tree,
  Alert,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  FolderOutlined,
  OrderedListOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Panel } = Collapse;

interface SpecificationTemplate {
  id: string;
  template_code: string;
  template_name: string;
  template_category: string;
  version: string;
  status: string;
  is_active: boolean;
}

interface ItemSpecification {
  id: string;
  specification_number: string;
  item_name: string;
  specification_name: string;
  version: string;
  approval_status: string;
  material_certificates_required: boolean;
  inspection_required: boolean;
}

interface TechnicalRequirement {
  id: string;
  requirement_code: string;
  requirement_name: string;
  requirement_category: string;
  is_mandatory: boolean;
  is_active: boolean;
}

const SpecificationBuilderPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [templates, setTemplates] = useState<SpecificationTemplate[]>([]);
  const [itemSpecifications, setItemSpecifications] = useState<ItemSpecification[]>([]);
  const [technicalRequirements, setTechnicalRequirements] = useState<TechnicalRequirement[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [selectedSpecification, setSelectedSpecification] = useState<any>(null);

  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [sectionModalVisible, setSectionModalVisible] = useState(false);
  const [parameterModalVisible, setParameterModalVisible] = useState(false);
  const [specificationModalVisible, setSpecificationModalVisible] = useState(false);
  const [requirementModalVisible, setRequirementModalVisible] = useState(false);
  const [templateDetailsModalVisible, setTemplateDetailsModalVisible] = useState(false);
  const [specificationDetailsModalVisible, setSpecificationDetailsModalVisible] = useState(false);

  const [templateForm] = Form.useForm();
  const [sectionForm] = Form.useForm();
  const [parameterForm] = Form.useForm();
  const [specificationForm] = Form.useForm();
  const [requirementForm] = Form.useForm();

  useEffect(() => {
    fetchTemplates();
    fetchItemSpecifications();
    fetchTechnicalRequirements();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/procurement/specification-templates');
      setTemplates(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch templates');
    }
  };

  const fetchItemSpecifications = async () => {
    setLoading(true);
    try {
      // This would need item selection first
      // For now, showing structure
      setItemSpecifications([]);
    } catch (error) {
      message.error('Failed to fetch specifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchTechnicalRequirements = async () => {
    try {
      const response = await apiClient.get('/procurement/technical-requirements');
      setTechnicalRequirements(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch requirements');
    }
  };

  const fetchTemplateDetails = async (id: string) => {
    try {
      const response = await apiClient.get(`/procurement/specification-templates/${id}`);
      setSelectedTemplate(response.data.data);
      setTemplateDetailsModalVisible(true);
    } catch (error) {
      message.error('Failed to fetch template details');
    }
  };

  const handleCreateTemplate = async (values: any) => {
    try {
      await apiClient.post('/procurement/specification-templates', {
        template_code: values.template_code,
        template_name: values.template_name,
        template_category: values.template_category,
        version: values.version || '1.0',
        description: values.description,
        applicable_standards: values.applicable_standards,
        reference_documents: values.reference_documents,
      });
      message.success('Template created successfully');
      setTemplateModalVisible(false);
      templateForm.resetFields();
      fetchTemplates();
    } catch (error) {
      message.error('Failed to create template');
    }
  };

  const handleAddSection = async (values: any) => {
    if (!selectedTemplate) return;

    try {
      await apiClient.post(
        `/procurement/specification-templates/${selectedTemplate.id}/sections`,
        {
          sections: [
            {
              section_code: values.section_code,
              section_name: values.section_name,
              section_order: values.section_order,
              description: values.description,
              is_mandatory: values.is_mandatory || false,
              compliance_required: values.compliance_required !== false,
            },
          ],
        }
      );
      message.success('Section added successfully');
      setSectionModalVisible(false);
      sectionForm.resetFields();
      fetchTemplateDetails(selectedTemplate.id);
    } catch (error) {
      message.error('Failed to add section');
    }
  };

  const handleCreateItemSpecification = async (values: any) => {
    try {
      await apiClient.post(`/procurement/items/${values.item_id}/specifications`, {
        template_id: values.template_id,
        specification_name: values.specification_name,
        version: values.version || '1.0',
        specification_type: values.specification_type,
        applicable_codes: values.applicable_codes,
        client_specification: values.client_specification,
        material_certificates_required: values.material_certificates_required || false,
        inspection_required: values.inspection_required || false,
        testing_required: values.testing_required || false,
        specification_data: values.specification_data,
      });
      message.success('Item specification created successfully');
      setSpecificationModalVisible(false);
      specificationForm.resetFields();
      fetchItemSpecifications();
    } catch (error) {
      message.error('Failed to create specification');
    }
  };

  const handleApproveSpecification = async (specificationId: string) => {
    try {
      await apiClient.post(`/procurement/specifications/${specificationId}/approve`, {});
      message.success('Specification approved successfully');
      fetchItemSpecifications();
    } catch (error) {
      message.error('Failed to approve specification');
    }
  };

  const handleCreateTechnicalRequirement = async (values: any) => {
    try {
      await apiClient.post('/procurement/technical-requirements', {
        requirement_code: values.requirement_code,
        requirement_name: values.requirement_name,
        requirement_category: values.requirement_category,
        description: values.description,
        applicable_to: values.applicable_to || 'All',
        reference_standard: values.reference_standard,
        is_mandatory: values.is_mandatory || false,
        requirement_details: values.requirement_details,
        documents_required: values.documents_required,
      });
      message.success('Technical requirement created successfully');
      setRequirementModalVisible(false);
      requirementForm.resetFields();
      fetchTechnicalRequirements();
    } catch (error) {
      message.error('Failed to create requirement');
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig: { [key: string]: { color: string } } = {
      Draft: { color: 'default' },
      Active: { color: 'green' },
      Inactive: { color: 'red' },
      Approved: { color: 'green' },
      Pending: { color: 'orange' },
    };
    const config = statusConfig[status] || { color: 'default' };
    return <Tag color={config.color}>{status}</Tag>;
  };

  const templateColumns: ColumnsType<SpecificationTemplate> = [
    {
      title: 'Template Code',
      dataIndex: 'template_code',
      key: 'template_code',
      width: 150,
    },
    {
      title: 'Template Name',
      dataIndex: 'template_name',
      key: 'template_name',
      width: 250,
    },
    {
      title: 'Category',
      dataIndex: 'template_category',
      key: 'template_category',
      width: 150,
      render: (category) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active) => <Switch checked={active} disabled />,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => fetchTemplateDetails(record.id)}
          >
            View
          </Button>
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedTemplate(record);
              setSectionModalVisible(true);
            }}
          >
            Add Section
          </Button>
        </Space>
      ),
    },
  ];

  const specificationColumns: ColumnsType<ItemSpecification> = [
    {
      title: 'Spec Number',
      dataIndex: 'specification_number',
      key: 'specification_number',
      width: 150,
    },
    {
      title: 'Item',
      dataIndex: 'item_name',
      key: 'item_name',
      width: 200,
    },
    {
      title: 'Specification Name',
      dataIndex: 'specification_name',
      key: 'specification_name',
      width: 250,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'MTC Required',
      dataIndex: 'material_certificates_required',
      key: 'material_certificates_required',
      width: 120,
      render: (required) => (
        <Tag color={required ? 'red' : 'default'}>{required ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Inspection',
      dataIndex: 'inspection_required',
      key: 'inspection_required',
      width: 100,
      render: (required) => (
        <Tag color={required ? 'orange' : 'default'}>{required ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedSpecification(record);
              setSpecificationDetailsModalVisible(true);
            }}
          >
            View
          </Button>
          {record.approval_status === 'Draft' && (
            <Button
              type="link"
              size="small"
              onClick={() => handleApproveSpecification(record.id)}
            >
              Approve
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const requirementColumns: ColumnsType<TechnicalRequirement> = [
    {
      title: 'Requirement Code',
      dataIndex: 'requirement_code',
      key: 'requirement_code',
      width: 150,
    },
    {
      title: 'Requirement Name',
      dataIndex: 'requirement_name',
      key: 'requirement_name',
      width: 300,
    },
    {
      title: 'Category',
      dataIndex: 'requirement_category',
      key: 'requirement_category',
      width: 150,
      render: (category) => <Tag>{category}</Tag>,
    },
    {
      title: 'Mandatory',
      dataIndex: 'is_mandatory',
      key: 'is_mandatory',
      width: 100,
      render: (mandatory) => (
        <Tag color={mandatory ? 'red' : 'default'}>{mandatory ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active) => <Switch checked={active} disabled />,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>Specification Builder</h1>
          <p style={{ color: '#8c8c8c' }}>
            Create specification templates, define item specifications, and manage technical requirements
          </p>
        </div>
        <Space>
          <Button icon={<PlusOutlined />} onClick={() => setTemplateModalVisible(true)}>
            Create Template
          </Button>
          <Button icon={<PlusOutlined />} onClick={() => setSpecificationModalVisible(true)}>
            Create Item Spec
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setRequirementModalVisible(true)}>
            Create Requirement
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <FileTextOutlined /> Templates ({templates.length})
            </span>
          }
          key="1"
        >
          <Card>
            <Table
              columns={templateColumns}
              dataSource={templates}
              rowKey="id"
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <OrderedListOutlined /> Item Specifications ({itemSpecifications.length})
            </span>
          }
          key="2"
        >
          <Card>
            <Table
              columns={specificationColumns}
              dataSource={itemSpecifications}
              rowKey="id"
              loading={loading}
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <SafetyCertificateOutlined /> Technical Requirements ({technicalRequirements.length})
            </span>
          }
          key="3"
        >
          <Card>
            <Table
              columns={requirementColumns}
              dataSource={technicalRequirements}
              rowKey="id"
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Create Template Modal */}
      <Modal
        title="Create Specification Template"
        open={templateModalVisible}
        onCancel={() => {
          setTemplateModalVisible(false);
          templateForm.resetFields();
        }}
        onOk={() => templateForm.submit()}
        width={700}
      >
        <Form form={templateForm} layout="vertical" onFinish={handleCreateTemplate}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="template_code"
                label="Template Code"
                rules={[{ required: true, message: 'Please enter code' }]}
              >
                <Input placeholder="e.g., SPEC-TEMP-001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="version" label="Version" initialValue="1.0">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="template_name"
            label="Template Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="e.g., Mechanical Parts Specification" />
          </Form.Item>

          <Form.Item name="template_category" label="Category">
            <Select placeholder="Select category">
              <Option value="Material">Material Specifications</Option>
              <Option value="Mechanical">Mechanical Parts</Option>
              <Option value="Electrical">Electrical Components</Option>
              <Option value="Chemical">Chemical Products</Option>
              <Option value="Service">Service Specifications</Option>
            </Select>
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Template description..." />
          </Form.Item>

          <Form.Item name="applicable_standards" label="Applicable Standards">
            <Input placeholder="e.g., ISO 9001, ASME, AWS" />
          </Form.Item>

          <Form.Item name="reference_documents" label="Reference Documents">
            <Input placeholder="Reference document numbers or codes" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Section Modal */}
      <Modal
        title={`Add Section to ${selectedTemplate?.template_name || 'Template'}`}
        open={sectionModalVisible}
        onCancel={() => {
          setSectionModalVisible(false);
          sectionForm.resetFields();
          setSelectedTemplate(null);
        }}
        onOk={() => sectionForm.submit()}
      >
        <Form form={sectionForm} layout="vertical" onFinish={handleAddSection}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="section_code"
                label="Section Code"
                rules={[{ required: true }]}
              >
                <Input placeholder="e.g., SEC-01" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="section_order" label="Order" initialValue={1}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="section_name"
            label="Section Name"
            rules={[{ required: true }]}
          >
            <Input placeholder="e.g., Material Properties" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Section description..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="is_mandatory" valuePropName="checked">
                <input type="checkbox" /> Mandatory Section
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="compliance_required" valuePropName="checked" initialValue={true}>
                <input type="checkbox" /> Compliance Required
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Create Item Specification Modal */}
      <Modal
        title="Create Item Specification"
        open={specificationModalVisible}
        onCancel={() => {
          setSpecificationModalVisible(false);
          specificationForm.resetFields();
        }}
        onOk={() => specificationForm.submit()}
        width={800}
      >
        <Form form={specificationForm} layout="vertical" onFinish={handleCreateItemSpecification}>
          <Form.Item
            name="item_id"
            label="Item"
            rules={[{ required: true, message: 'Please select item' }]}
          >
            <Select placeholder="Select Item">
              {/* Items will be loaded dynamically */}
            </Select>
          </Form.Item>

          <Form.Item name="template_id" label="Template (Optional)">
            <Select placeholder="Select template to use as base">
              {templates.map((template) => (
                <Option key={template.id} value={template.id}>
                  {template.template_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="specification_name"
                label="Specification Name"
                rules={[{ required: true }]}
              >
                <Input placeholder="e.g., ASTM A36 Steel Plate" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="version" label="Version" initialValue="1.0">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="specification_type" label="Specification Type">
            <Select placeholder="Select type">
              <Option value="Material">Material Specification</Option>
              <Option value="Manufacturing">Manufacturing Specification</Option>
              <Option value="Quality">Quality Specification</Option>
              <Option value="Performance">Performance Specification</Option>
            </Select>
          </Form.Item>

          <Form.Item name="applicable_codes" label="Applicable Codes/Standards">
            <Select mode="tags" placeholder="Enter codes (e.g., ASTM A36, ISO 9001)">
              {/* Tags will be added dynamically */}
            </Select>
          </Form.Item>

          <Form.Item name="client_specification" label="Client Specification Reference">
            <Input placeholder="Client specification number or reference" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="material_certificates_required" valuePropName="checked">
                <input type="checkbox" /> MTC Required
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="inspection_required" valuePropName="checked">
                <input type="checkbox" /> Inspection Required
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="testing_required" valuePropName="checked">
                <input type="checkbox" /> Testing Required
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="specification_data" label="Specification Data (JSON)">
            <TextArea
              rows={6}
              placeholder='Enter specification details as JSON, e.g., {"tensile_strength": {"min": 400, "max": 550}}'
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Technical Requirement Modal */}
      <Modal
        title="Create Technical Requirement"
        open={requirementModalVisible}
        onCancel={() => {
          setRequirementModalVisible(false);
          requirementForm.resetFields();
        }}
        onOk={() => requirementForm.submit()}
        width={700}
      >
        <Form form={requirementForm} layout="vertical" onFinish={handleCreateTechnicalRequirement}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="requirement_code"
                label="Requirement Code"
                rules={[{ required: true }]}
              >
                <Input placeholder="e.g., TR-001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="requirement_category"
                label="Category"
                rules={[{ required: true }]}
              >
                <Select placeholder="Select category">
                  <Option value="Documentation">Documentation</Option>
                  <Option value="Inspection">Inspection</Option>
                  <Option value="Material-Cert">Material Certification</Option>
                  <Option value="Testing">Testing</Option>
                  <Option value="Quality">Quality Control</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="requirement_name"
            label="Requirement Name"
            rules={[{ required: true }]}
          >
            <Input placeholder="e.g., Material Test Certificate (MTC)" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Detailed requirement description..." />
          </Form.Item>

          <Form.Item name="applicable_to" label="Applicable To" initialValue="All">
            <Select>
              <Option value="All">All Items</Option>
              <Option value="Materials">Materials Only</Option>
              <Option value="Equipment">Equipment Only</Option>
              <Option value="Services">Services Only</Option>
            </Select>
          </Form.Item>

          <Form.Item name="reference_standard" label="Reference Standard">
            <Input placeholder="e.g., ISO 10474, EN 10204" />
          </Form.Item>

          <Form.Item name="documents_required" label="Documents Required">
            <Select mode="tags" placeholder="Enter required documents">
              {/* Tags will be added dynamically */}
            </Select>
          </Form.Item>

          <Form.Item name="is_mandatory" valuePropName="checked">
            <input type="checkbox" /> Mandatory Requirement
          </Form.Item>
        </Form>
      </Modal>

      {/* Template Details Modal */}
      <Modal
        title={`Template: ${selectedTemplate?.template_name || ''}`}
        open={templateDetailsModalVisible}
        onCancel={() => {
          setTemplateDetailsModalVisible(false);
          setSelectedTemplate(null);
        }}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setTemplateDetailsModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="addSection"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setSectionModalVisible(true)}
          >
            Add Section
          </Button>,
        ]}
      >
        {selectedTemplate && (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Template Code">{selectedTemplate.template_code}</Descriptions.Item>
              <Descriptions.Item label="Version">{selectedTemplate.version}</Descriptions.Item>
              <Descriptions.Item label="Category">{selectedTemplate.template_category}</Descriptions.Item>
              <Descriptions.Item label="Status">{getStatusTag(selectedTemplate.status)}</Descriptions.Item>
              <Descriptions.Item label="Standards" span={2}>
                {selectedTemplate.applicable_standards || '-'}
              </Descriptions.Item>
            </Descriptions>

            {selectedTemplate.sections && selectedTemplate.sections.length > 0 && (
              <Card title="Sections & Parameters" style={{ marginTop: 16 }}>
                <Collapse accordion>
                  {selectedTemplate.sections.map((section: any, index: number) => (
                    <Panel
                      header={
                        <Space>
                          <FolderOutlined />
                          <span>
                            {section.section_name}
                            {section.is_mandatory && <Tag color="red" style={{ marginLeft: 8 }}>Mandatory</Tag>}
                          </span>
                        </Space>
                      }
                      key={index}
                    >
                      <p>{section.description}</p>
                      {section.parameters && section.parameters.length > 0 ? (
                        <List
                          size="small"
                          dataSource={section.parameters}
                          renderItem={(param: any) => (
                            <List.Item>
                              <List.Item.Meta
                                title={
                                  <Space>
                                    {param.parameter_name}
                                    {param.is_mandatory && <Tag color="red">Required</Tag>}
                                    {param.is_critical && <Tag color="orange">Critical</Tag>}
                                  </Space>
                                }
                                description={
                                  <div>
                                    <div>Type: {param.value_type} | Unit: {param.unit_of_measure || 'N/A'}</div>
                                    {param.min_value && param.max_value && (
                                      <div>Range: {param.min_value} - {param.max_value}</div>
                                    )}
                                    {param.test_method && <div>Test Method: {param.test_method}</div>}
                                  </div>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      ) : (
                        <Alert message="No parameters defined" type="info" />
                      )}
                    </Panel>
                  ))}
                </Collapse>
              </Card>
            )}
          </>
        )}
      </Modal>

      {/* Specification Details Modal */}
      <Modal
        title={`Specification: ${selectedSpecification?.specification_number || ''}`}
        open={specificationDetailsModalVisible}
        onCancel={() => {
          setSpecificationDetailsModalVisible(false);
          setSelectedSpecification(null);
        }}
        width={800}
        footer={[
          <Button key="close" onClick={() => setSpecificationDetailsModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedSpecification && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Item">{selectedSpecification.item_name}</Descriptions.Item>
              <Descriptions.Item label="Version">{selectedSpecification.version}</Descriptions.Item>
              <Descriptions.Item label="Status">
                {getStatusTag(selectedSpecification.approval_status)}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {selectedSpecification.specification_type || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="MTC Required" span={2}>
                <Tag color={selectedSpecification.material_certificates_required ? 'red' : 'default'}>
                  {selectedSpecification.material_certificates_required ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Modal>
    </div>
  );
};

export default SpecificationBuilderPage;
